import { useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { EnterKind } from './transitions';

/**
 * The app's own map of this tab's browser history: which screen sits at each history index (the router keeps the
 * index in `history.state.__TSR_index`, so pushes, replaces and Back/Forward are all told apart). With it the in-app
 * back button does what the browser's does, transitions know their direction, and leaving the booking steps during
 * a ride rewinds to Home in one step instead of piling up entries. Kept in sessionStorage, so a refresh remembers it.
 */
const KEY = 'zr-hist';
const TABS = new Set(['/home', '/trips', '/account']);
let entries: Record<number, string> = load();
let at = -1;
let shown = '';
let kind: EnterKind = 'fade';

function load(): Record<number, string> {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<number, string>;
  } catch {
    return {};
  }
}
const save = () => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    /* ignore */
  }
};
const indexNow = (): number => {
  if (typeof window === 'undefined') return 0;
  const i = (window.history.state as { __TSR_index?: number } | null)?.__TSR_index;
  return typeof i === 'number' ? i : 0;
};

/** How a screen comes in, from where it sits in the history relative to the one before. */
function kindFor(prevPath: string, prevAt: number, path: string, idx: number): EnterKind {
  if (!prevPath) return 'fade';
  if (idx < prevAt) return 'back';
  if (TABS.has(prevPath) && TABS.has(path)) return 'fade';
  if (path === '/ride') return 'rise';
  return 'push';
}

/** The screen on show is `path`: record it once and say how it comes in. */
export function enterFor(path: string): EnterKind {
  const idx = indexNow();
  if (shown === path && at === idx) return kind;
  kind = kindFor(shown, at, path, idx);
  // a new entry drops the forward history after it
  if (idx > at && at >= 0) for (const k of Object.keys(entries)) if (Number(k) > idx) delete entries[Number(k)];
  entries[idx] = path;
  at = idx;
  shown = path;
  save();
  return kind;
}

/** How a move to `path` would come in, without recording it (for the View Transition types). */
export const peekEnter = (path: string): EnterKind => {
  if (path === shown) return kind;
  if (entries[at - 1] === path) return 'back';
  return kindFor(shown, at, path, at + 1);
};

/** There is an app screen behind this one in the browser history. */
export const canGoBack = () => at > 0 && !!entries[at - 1];

/**
 * Steps back through the browser history to the nearest `target` behind us (one history.go, no new entries) and
 * records it as a "back" move. False when it isn't there (a deep link): the caller replaces instead.
 */
export function rewindTo(target: string): boolean {
  let i = at - 1;
  while (i >= 0 && entries[i] !== undefined && entries[i] !== target) i--;
  if (i < 0 || entries[i] !== target || typeof window === 'undefined') return false;
  kind = 'back';
  window.history.go(i - at);
  return true;
}

/**
 * The ride this tab has just booked, on its way from Confirm pickup to the live ride: the "an open ride owns the
 * booking steps" rule must not send it Home in the moment before /ride shows.
 */
let booked: string | null = null;
export const markBooked = (id: string | null) => {
  booked = id;
};
export const justBooked = () => booked;

/** For tests. */
export const resetNav = () => {
  entries = {};
  at = -1;
  shown = '';
  kind = 'fade';
  save();
};

/** Esc goes back a step on a keyboard (unless a sheet is open — Esc closes that first, or the rider is typing). */
export function useEscapeBack(back: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back]);
}

/**
 * The in-app back button: the browser's back when the screen behind is ours, else (opened from a link) the
 * screen that would be behind, replacing this entry so Back never leaves a loop.
 */
export function useBack(fallback: string) {
  const nav = useNavigate();
  return useCallback(() => {
    if (canGoBack()) window.history.back();
    else void nav({ to: fallback, replace: true });
  }, [nav, fallback]);
}
